import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  CheckCircle2,
  Package,
  MapPin,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Scan,
  RefreshCw,
  Send,
  ShieldCheck,
  Zap,
  List,
  Search
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import { userProfileService } from '../../../../services/userProfileService';
import { CountingListOverviewItem, CountingListLine } from '../../../../types/countingListCreation';
import { parseGS1Barcode } from '../../../../utils/gs1Parser';
import { normalizeBinCode } from '../../../../utils/binCode';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput, MobileBarcodeCaptureInputHandle } from '../../../common/scanning';
import { NumericInput, ScanTextInput } from '../../../common/inputs';
import { itemsService } from '../../../../services/itemsService';
import { inventoryService, ItemLocationInfo } from '../../../../services/inventoryService';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';

interface CountingListCountingProps {
  list: CountingListOverviewItem;
  onBack: () => void;
  onCompleted?: () => void;
}

interface CountingLine extends CountingListLine {
  isExpanded?: boolean;
  isCounting?: boolean;
}

type ConfirmStep = 'location' | 'item' | 'count';
type ConfirmableStep = 'item' | 'location';

interface RecentCount {
  lineID: number;
  itemNo: string;
  description1?: string;
  locationName?: string;
  itemUnitCode?: string;
  quantity: number;
}

interface UnknownItemInfo {
  itemNo: string;
  locations: ItemLocationInfo[];
  loading: boolean;
}

const QUICK_MODE_STORAGE_KEY = 'countingList.quickMode';
const RECENT_COUNTS_MAX = 10;
const COUNTED_MODAL_RENDER_MAX = 50;

// GS1-parsing av skannet verdi (delt mellom vanlig og hurtig modus)
const extractScannedItemNo = (value: string): { rawScanned: string; scannedItemNo: string } => {
  const looksLikeGS1 = value.includes(String.fromCharCode(29)) || value.includes('~') || value.includes('§') || value.startsWith(']C1');
  const rawScanned = value.replace(/[~§]+$/g, '').trim();
  let scannedItemNo = rawScanned;
  if (looksLikeGS1) {
    const gs1Data = parseGS1Barcode(value);
    if (gs1Data.gtin) {
      scannedItemNo = gs1Data.gtin;
    }
  }
  return { rawScanned, scannedItemNo };
};

// Vare-match med GTIN-13/14-padding (AI(01) gir alltid 14 siffer: ledende 0 + GTIN-13)
const matchesScannedItem = (line: CountingListLine, scannedLower: string): boolean => {
  const lineItemNo = line.itemNo?.toLowerCase() || '';
  const lineBarCode = line.itemBarCode?.toLowerCase() || '';
  if (lineItemNo === scannedLower || lineBarCode === scannedLower) return true;
  if (scannedLower.length === 14) {
    if (lineBarCode.length === 13 && '0' + lineBarCode === scannedLower) return true;
    if (lineItemNo.length === 13 && '0' + lineItemNo === scannedLower) return true;
  }
  if (scannedLower.length === 13) {
    if (lineBarCode.length === 14 && lineBarCode === '0' + scannedLower) return true;
    if (lineItemNo.length === 14 && lineItemNo === '0' + scannedLower) return true;
  }
  return false;
};

export const CountingListCounting: React.FC<CountingListCountingProps> = ({ list, onBack, onCompleted }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [lines, setLines] = useState<CountingLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeLineId, setActiveLineId] = useState<number | null>(null);
  const [countInput, setCountInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error'; detail?: string } | null>(null);
  const countInputRef = useRef<HTMLInputElement>(null);
  const locationScanRef = useRef<HTMLInputElement>(null);
  const itemScanRef = useRef<HTMLInputElement>(null);
  const mobileCaptureRef = useRef<MobileBarcodeCaptureInputHandle>(null);
  const barcodeScannerEnabled = useBarcodeScannerEnabled();

  // Barcode confirm settings from user profile
  const [requireLocationScan, setRequireLocationScan] = useState(false);
  const [requireItemScan, setRequireItemScan] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Hurtigtelling (TW 36373390): skann = talt, ingen bekreftelsessteg, aldri full linjeliste
  const [quickMode, setQuickMode] = useState<boolean>(() => {
    try { return localStorage.getItem(QUICK_MODE_STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [recentCounts, setRecentCounts] = useState<RecentCount[]>([]);
  const [showCountedModal, setShowCountedModal] = useState(false);
  const [countedFilter, setCountedFilter] = useState('');
  const [unknownItem, setUnknownItem] = useState<UnknownItemInfo | null>(null);
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickManualInput, setQuickManualInput] = useState('');

  // Confirmation step state
  const [confirmStep, setConfirmStep] = useState<ConfirmStep>('count');
  const [locationScanInput, setLocationScanInput] = useState('');
  const [itemScanInput, setItemScanInput] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  // Steg som allerede er bekreftet via skann for aktiv linje (dobbel-skann = bekreftelse)
  const [confirmedSteps, setConfirmedSteps] = useState<Set<ConfirmableStep>>(new Set());
  const scanHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load user profile settings
  // («Maks antall per linje» håndheves av NumericInput med quantity-prop — samme sperre som i øvrige moduler)
  useEffect(() => {
    userProfileService.getCurrentUserProfile().then(profile => {
      if (profile) {
        setRequireLocationScan(profile.barCodeConfirmLocation ?? false);
        setRequireItemScan(profile.barCodeConfirmItem ?? false);
      }
      setProfileLoaded(true);
    }).catch(() => {
      setProfileLoaded(true);
    });
  }, []);

  // Determine the first confirm step based on settings, skipping steps
  // already confirmed via scan (dobbel-skann = bekreftelse)
  // Order: Item → Location → Count
  const getFirstConfirmStep = useCallback((confirmed: Set<ConfirmableStep>): ConfirmStep => {
    if (requireItemScan && !confirmed.has('item')) return 'item';
    if (requireLocationScan && !confirmed.has('location')) return 'location';
    return 'count';
  }, [requireLocationScan, requireItemScan]);

  // Determine next step after current, skipping already confirmed steps
  // Order: Item → Location → Count
  const getNextStep = useCallback((current: ConfirmStep, confirmed: Set<ConfirmableStep>): ConfirmStep => {
    if (current === 'item') return requireLocationScan && !confirmed.has('location') ? 'location' : 'count';
    if (current === 'location') return 'count';
    return 'count';
  }, [requireLocationScan]);

  const loadLines = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await countingListCreationService.getCountingListLines(list.batchID);
      setLines(result.map(l => ({ ...l, isExpanded: false, isCounting: false })));
    } catch (err) {
      console.error('Error loading counting list lines:', err);
      setError(t('warehouse.modules.inventory.countList.loadLinesFailed', {}, 'Kunne ikke laste tellelistelinjer'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLines();
  }, [list.batchID]);

  // Rydd opp hint-timer ved unmount
  useEffect(() => {
    return () => {
      if (scanHintTimerRef.current) {
        clearTimeout(scanHintTimerRef.current);
      }
    };
  }, []);

  // Focus the appropriate input based on confirm step.
  // Ikke når global skanning er aktiv — fokus i et input-felt ville både tvinge frem
  // virtuelt tastatur på nettbrett og hindre den globale skannerhooken i å fange skann.
  useEffect(() => {
    if (activeLineId === null || barcodeScannerEnabled) return;
    if (confirmStep === 'count' && countInputRef.current) {
      countInputRef.current.focus();
      countInputRef.current.select();
    } else if (confirmStep === 'location' && locationScanRef.current) {
      locationScanRef.current.focus();
    } else if (confirmStep === 'item' && itemScanRef.current) {
      itemScanRef.current.focus();
    }
  }, [activeLineId, confirmStep, barcodeScannerEnabled]);

  // Find the active line object
  const activeLine = useMemo(() => {
    if (activeLineId === null) return null;
    return lines.find(l => l.lineID === activeLineId) || null;
  }, [activeLineId, lines]);

  // Find the next uncounted line (for the "next" suggestion)
  const nextUncountedLine = useMemo(() => {
    return lines.find(l => l.lineStatus < 50);
  }, [lines]);

  // Modaler som skal blokkere global skann-fangst (isAnyModalOpen-mønsteret)
  const isAnyModalOpen = showCountedModal;

  // Global barcode scanner — aktiv i alle steg (rutes etter tilstand), men ikke når modal er åpen
  useSimpleBarcodeScanner({
    enabled: barcodeScannerEnabled && !isAnyModalOpen,
    onScan: (barcode: string) => {
      handleGlobalScan(barcode);
    }
  });

  // Rut skann etter aktivt steg
  const handleGlobalScan = (barcode: string) => {
    if (!barcode.trim() || isAnyModalOpen) return;

    if (quickMode) {
      handleQuickScan(barcode);
      return;
    }

    if (activeLineId === null) {
      handleBarcodeScan(barcode);
      return;
    }

    if (confirmStep === 'item') {
      setItemScanInput(barcode);
      handleItemConfirm(barcode);
      return;
    }

    if (confirmStep === 'location') {
      setLocationScanInput(barcode);
      handleLocationConfirm(barcode);
      return;
    }

    // Antall-steget: blokker skannet — en EAN-13 ville ellers kunne lagres som antall
    if (scanHintTimerRef.current) {
      clearTimeout(scanHintTimerRef.current);
    }
    setScanError(t('warehouse.modules.inventory.countList.scanBlockedInCount', {}, 'Skann registrert — tast antall manuelt'));
    scanHintTimerRef.current = setTimeout(() => {
      setScanError(null);
      scanHintTimerRef.current = null;
    }, 2500);
  };

  const handleBarcodeScan = async (value: string) => {
    if (!value.trim()) return;

    const { rawScanned, scannedItemNo } = extractScannedItemNo(value);

    // Find a matching uncounted line (GTIN-13/14-padding håndteres i matchesScannedItem)
    const scannedLower = scannedItemNo.toLowerCase();
    const matchingLine = lines.find(l => l.lineStatus < 50 && matchesScannedItem(l, scannedLower));

    if (matchingLine) {
      // Skann identifiserte linjen via VARE — varesteget er dermed bekreftet
      startCountingLine(matchingLine.lineID, ['item']);
      setMessage({ text: t('warehouse.modules.inventory.countList.itemFound', { '0': matchingLine.itemNo, '1': matchingLine.description1 || '' }, `Vare funnet: ${matchingLine.itemNo} - ${matchingLine.description1 || ''}`), type: 'success' });
      return;
    }

    // Lokasjonsskann-start: skann av hylleetikett starter første utellede linje på lokasjonen.
    // Normaliser begge sider ($-prefiks hos f.eks. SafeNor finnes kun i lagrede verdier).
    const normalizedScan = normalizeBinCode(rawScanned);
    if (normalizedScan) {
      const locationLine = lines.find(l =>
        l.lineStatus < 50 && normalizeBinCode(l.locationName || '') === normalizedScan
      );
      if (locationLine) {
        // Skann identifiserte linjen via LOKASJON — lokasjonssteget er dermed bekreftet
        startCountingLine(locationLine.lineID, ['location']);
        setMessage({ text: t('warehouse.modules.inventory.countList.locationFound', { '0': locationLine.locationName || '' }, `Lokasjon funnet: ${locationLine.locationName || ''}`), type: 'success' });
        return;
      }
    }

    // TW 36320099: fallback — slå opp koden i Items/ItemUnits (alternative strekkoder) og
    // match tellelinjene på resolvet varenummer. Serveren håndterer GTIN-13/14-variantene.
    let resolvedItemNo = scannedItemNo;
    try {
      const resolved = await itemsService.resolveBarcode(scannedItemNo);
      if (resolved.length > 0 && resolved[0].itemNo) {
        resolvedItemNo = resolved[0].itemNo;
        const resolvedLower = resolvedItemNo.toLowerCase();
        const resolvedLine = lines.find(l => l.lineStatus < 50 && l.itemNo?.toLowerCase() === resolvedLower);
        if (resolvedLine) {
          startCountingLine(resolvedLine.lineID, ['item']);
          setMessage({ text: t('warehouse.modules.inventory.countList.itemFound', { '0': resolvedLine.itemNo, '1': resolvedLine.description1 || '' }, `Vare funnet: ${resolvedLine.itemNo} - ${resolvedLine.description1 || ''}`), type: 'success' });
          return;
        }
      }
    } catch (err) {
      console.warn('Barcode resolve fallback failed:', err);
    }

    // TW 36373390: uventet vare — vis også hvor Compass mener varen er registrert
    handleUnknownItem(scannedItemNo, resolvedItemNo);
  };

  // Uventet vare: feilmelding + lokasjonsoppslag (hvor Compass har varen registrert)
  const handleUnknownItem = async (scannedItemNo: string, resolvedItemNo: string) => {
    setMessage({ text: t('warehouse.modules.inventory.countList.noUncountedLineFound', { '0': scannedItemNo }, `Ingen utellet linje funnet for: ${scannedItemNo}`), type: 'error' });
    setUnknownItem({ itemNo: resolvedItemNo, locations: [], loading: true });
    const locations = await inventoryService.getLocationsForItem(resolvedItemNo);
    setUnknownItem(prev => prev && prev.itemNo === resolvedItemNo
      ? { itemNo: resolvedItemNo, locations, loading: false }
      : prev);
  };

  // ===== Hurtigtelling (TW 36373390) =====

  const toggleQuickMode = () => {
    const next = !quickMode;
    setQuickMode(next);
    try { localStorage.setItem(QUICK_MODE_STORAGE_KEY, next ? '1' : '0'); } catch { /* localStorage utilgjengelig */ }
    cancelCounting();
    setUnknownItem(null);
    setMessage(null);
  };

  // Registrer telling direkte (skann = bekreftelse). Samme endepunkt som vanlig telling.
  const quickRecordCount = async (line: CountingLine, newQty: number) => {
    setQuickSaving(true);
    try {
      await countingListCreationService.recordLineCount(list.batchID, line.lineID, newQty);
      setLines(prev => prev.map(l =>
        l.lineID === line.lineID ? { ...l, quantity: newQty, lineStatus: 50 } : l
      ));
      setRecentCounts(prev => [
        { lineID: line.lineID, itemNo: line.itemNo, description1: line.description1, locationName: line.locationName, itemUnitCode: line.itemUnitCode, quantity: newQty },
        ...prev.filter(r => r.lineID !== line.lineID)
      ].slice(0, RECENT_COUNTS_MAX));
      setMessage({ text: t('warehouse.modules.inventory.countList.quickCountRecorded', { '0': line.itemNo, '1': `${newQty} ${line.itemUnitCode || 'stk'}` }, `Talt: ${line.itemNo} — ${newQty} ${line.itemUnitCode || 'stk'}`), type: 'success', detail: line.description1 || undefined });
    } catch (err) {
      console.error('Error saving quick count:', err);
      setMessage({ text: t('warehouse.modules.inventory.countList.saveCountFailed', {}, 'Feil ved lagring av telling'), type: 'error' });
    } finally {
      setQuickSaving(false);
    }
  };

  const handleQuickScan = async (value: string) => {
    if (!value.trim() || quickSaving) return;
    setUnknownItem(null);

    const { rawScanned, scannedItemNo } = extractScannedItemNo(value);
    const scannedLower = scannedItemNo.toLowerCase();

    // 1) Utellet linje → talt 1. 2) Allerede talt linje → +1 per skann.
    let line = lines.find(l => l.lineStatus < 50 && matchesScannedItem(l, scannedLower))
      ?? lines.find(l => l.lineStatus >= 50 && matchesScannedItem(l, scannedLower));

    // 3) Lokasjonsskann — kun informasjon i hurtigmodus (linjen velges av vareskannet)
    if (!line) {
      const normalizedScan = normalizeBinCode(rawScanned);
      if (normalizedScan && lines.some(l => normalizeBinCode(l.locationName || '') === normalizedScan)) {
        setMessage({ text: t('warehouse.modules.inventory.countList.locationFound', { '0': rawScanned }, `Lokasjon funnet: ${rawScanned}`), type: 'success' });
        return;
      }
    }

    // 4) Fallback: alternative strekkoder (ItemUnits) — som i vanlig modus (TW 36320099)
    let resolvedItemNo = scannedItemNo;
    if (!line) {
      try {
        const resolved = await itemsService.resolveBarcode(scannedItemNo);
        if (resolved.length > 0 && resolved[0].itemNo) {
          resolvedItemNo = resolved[0].itemNo;
          const resolvedLower = resolvedItemNo.toLowerCase();
          line = lines.find(l => l.lineStatus < 50 && l.itemNo?.toLowerCase() === resolvedLower)
            ?? lines.find(l => l.lineStatus >= 50 && l.itemNo?.toLowerCase() === resolvedLower);
        }
      } catch (err) {
        console.warn('Barcode resolve fallback failed:', err);
      }
    }

    if (line) {
      const newQty = line.lineStatus >= 50 ? (line.quantity ?? 0) + 1 : 1;
      await quickRecordCount(line, newQty);
      return;
    }

    handleUnknownItem(scannedItemNo, resolvedItemNo);
  };

  // Start counting a line - enters confirmation flow.
  // preConfirmed: steg som allerede er bekreftet via identifiserende skann (dobbel-skann = bekreftelse)
  const startCountingLine = (lineId: number, preConfirmed?: ConfirmableStep[]) => {
    const confirmed = new Set<ConfirmableStep>(preConfirmed ?? []);
    setConfirmedSteps(confirmed);
    setActiveLineId(lineId);
    setCountInput('');
    setLocationScanInput('');
    setItemScanInput('');
    setScanError(null);
    setConfirmStep(getFirstConfirmStep(confirmed));
  };

  const handleStartCounting = (lineId: number) => {
    if (activeLineId === lineId) {
      // Toggle off
      cancelCounting();
    } else {
      startCountingLine(lineId);
    }
  };

  const cancelCounting = () => {
    setActiveLineId(null);
    setCountInput('');
    setLocationScanInput('');
    setItemScanInput('');
    setScanError(null);
    setConfirmedSteps(new Set());
    setConfirmStep('count');
  };

  // Location scan confirmation (value fra globalt skann, ellers feltets state)
  const handleLocationConfirm = (value?: string) => {
    if (!activeLine) return;
    const input = value ?? locationScanInput;
    const expected = normalizeBinCode(activeLine.locationName || '');
    const scanned = normalizeBinCode(input);

    if (!scanned) {
      setScanError(t('warehouse.modules.inventory.countList.scanOrTypeLocation', {}, 'Skann eller skriv inn lokasjon'));
      return;
    }

    if (scanned !== expected) {
      setScanError(t('warehouse.modules.inventory.countList.wrongLocation', { '0': input, '1': activeLine.locationName }, `Feil lokasjon: "${input}". Forventet: "${activeLine.locationName}"`));
      return;
    }

    // Location confirmed — advance to next step
    setScanError(null);
    const confirmed = new Set(confirmedSteps).add('location');
    setConfirmedSteps(confirmed);
    setConfirmStep(getNextStep('location', confirmed));
    // Ble bekreftelsen tastet i feltet, unmountes feltet nå med fokuset — gi det tilbake til capture-inputen
    setTimeout(() => mobileCaptureRef.current?.focus(), 150);
  };

  const handleLocationOverride = () => {
    // Allow override — skip location check
    setScanError(null);
    const confirmed = new Set(confirmedSteps).add('location');
    setConfirmedSteps(confirmed);
    setConfirmStep(getNextStep('location', confirmed));
  };

  // Item scan confirmation (value fra globalt skann, ellers feltets state)
  const handleItemConfirm = async (value?: string) => {
    if (!activeLine) return;
    const input = value ?? itemScanInput;
    const scanned = input.toLowerCase().trim();

    if (!scanned) {
      setScanError(t('warehouse.modules.inventory.countList.scanOrTypeItem', {}, 'Skann eller skriv inn varenummer'));
      return;
    }

    const lineItemNo = activeLine.itemNo.toLowerCase().trim();
    const lineBarCode = activeLine.itemBarCode?.toLowerCase().trim() || '';
    // Exact match
    let matchesItemNo = scanned === lineItemNo;
    let matchesBarcode = !!lineBarCode && scanned === lineBarCode;
    // GTIN-13/14 padding match
    if (!matchesItemNo && !matchesBarcode) {
      if (scanned.length === 14) {
        matchesBarcode = lineBarCode.length === 13 && '0' + lineBarCode === scanned;
        matchesItemNo = lineItemNo.length === 13 && '0' + lineItemNo === scanned;
      } else if (scanned.length === 13) {
        matchesBarcode = lineBarCode.length === 14 && lineBarCode === '0' + scanned;
        matchesItemNo = lineItemNo.length === 14 && lineItemNo === '0' + scanned;
      }
    }

    // TW 36320099: fallback — alternative strekkoder (ItemUnits) godtas ved varebekreftelse.
    if (!matchesItemNo && !matchesBarcode) {
      try {
        const resolved = await itemsService.resolveBarcode(input.trim());
        if (resolved.length > 0 && resolved[0].itemNo?.toLowerCase() === lineItemNo) {
          matchesItemNo = true;
        }
      } catch (err) {
        console.warn('Barcode resolve fallback failed:', err);
      }
    }

    if (!matchesItemNo && !matchesBarcode) {
      const expectedItem = `${activeLine.itemNo}${activeLine.itemBarCode ? ` (${activeLine.itemBarCode})` : ''}`;
      setScanError(t('warehouse.modules.inventory.countList.wrongItem', { '0': input, '1': expectedItem }, `Feil vare: "${input}". Forventet: "${expectedItem}"`));
      return;
    }

    // Item confirmed — advance to next step (location if required, otherwise count)
    setScanError(null);
    const confirmed = new Set(confirmedSteps).add('item');
    setConfirmedSteps(confirmed);
    setConfirmStep(getNextStep('item', confirmed));
    // Ble bekreftelsen tastet i feltet, unmountes feltet nå med fokuset — gi det tilbake til capture-inputen
    setTimeout(() => mobileCaptureRef.current?.focus(), 150);
  };

  const handleSaveCount = async (lineId: number) => {
    const qty = parseFloat(countInput);
    if (isNaN(qty) || qty < 0) {
      setMessage({ text: t('warehouse.modules.inventory.countList.invalidQuantity', {}, 'Ugyldig antall'), type: 'error' });
      return;
    }

    setSaving(true);
    try {
      await countingListCreationService.recordLineCount(list.batchID, lineId, qty);

      // Update local state
      setLines(prev => prev.map(l =>
        l.lineID === lineId
          ? { ...l, quantity: qty, lineStatus: 50 }
          : l
      ));

      cancelCounting();
      setMessage({ text: t('warehouse.modules.inventory.countList.countRecorded', { '0': qty }, `Telling registrert: ${qty}`), type: 'success' });

      // Antallsfeltet tok fokus fra den usynlige capture-inputen og unmountes nå —
      // gi fokus tilbake så neste skann fanges uten manuelt tapp (Zebra o.l.)
      setTimeout(() => mobileCaptureRef.current?.focus(), 150);

      // Auto-advance to next uncounted line after a short delay
      setTimeout(() => {
        setMessage(null);
        const updatedLines = lines.map(l =>
          l.lineID === lineId ? { ...l, lineStatus: 50 } : l
        );
        const next = updatedLines.find(l => l.lineStatus < 50);
        if (next) {
          startCountingLine(next.lineID);
        }
      }, 1500);
    } catch (err) {
      console.error('Error saving count:', err);
      setMessage({ text: t('warehouse.modules.inventory.countList.saveCountFailed', {}, 'Feil ved lagring av telling'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Reset a counted line so operator can recount
  const handleResetLine = async (lineId: number) => {
    const line = lines.find(l => l.lineID === lineId);
    if (!line) return;

    const confirmed = window.confirm(
      t('warehouse.modules.inventory.countList.confirmResetLine', { '0': line.itemNo, '1': line.locationName, '2': line.quantity ?? 0 }, `Vil du nullstille tellingen for ${line.itemNo} på lokasjon ${line.locationName} og telle på nytt?\n\nTelt antall (${line.quantity ?? 0}) vil bli fjernet.`)
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      await countingListCreationService.resetLineCount(list.batchID, lineId);

      // Update local state
      setLines(prev => prev.map(l =>
        l.lineID === lineId
          ? { ...l, lineStatus: 20, quantity: 0 }
          : l
      ));

      setMessage({ text: t('warehouse.modules.inventory.countList.lineReset', {}, 'Linje nullstilt — tell på nytt'), type: 'success' });

      // Start counting flow for this line (requires re-scan of item/location)
      startCountingLine(lineId);
    } catch (err) {
      console.error('Error resetting line count:', err);
      setMessage({ text: t('warehouse.modules.inventory.countList.resetLineFailed', {}, 'Feil ved nullstilling av tellelinje'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteList = async () => {
    const counted = lines.filter(l => l.lineStatus >= 50).length;
    const uncounted = lines.length - counted;

    let confirmMsg = t('warehouse.modules.inventory.countList.confirmCompleteIntro', { '0': counted, '1': lines.length }, `Fullfør tellelisten? ${counted} av ${lines.length} linjer er telt.`);
    if (uncounted > 0) {
      confirmMsg += t('warehouse.modules.inventory.countList.confirmCompleteUncounted', { '0': uncounted }, `\n\n⚠️ ${uncounted} linje(r) er ikke telt. Disse vil stå som 0.`);
    }
    confirmMsg += t('warehouse.modules.inventory.countList.confirmCompleteSentToManager', {}, '\n\nTellelisten sendes til lagersjef for gjennomgang.');

    if (!window.confirm(confirmMsg)) return;

    setCompleting(true);
    try {
      const result = await countingListCreationService.completeCountingList(list.batchID);
      if (result.success) {
        setMessage({ text: t('warehouse.modules.inventory.countList.listCompleted', {}, 'Tellelisten er fullført og sendt til lagersjef'), type: 'success' });
        setTimeout(() => {
          onCompleted?.();
          onBack();
        }, 1500);
      } else {
        setMessage({ text: result.errorMessage || t('warehouse.modules.inventory.countList.completeFailed', {}, 'Feil ved fullføring'), type: 'error' });
      }
    } catch (err) {
      console.error('Error completing counting list:', err);
      setMessage({ text: t('warehouse.modules.inventory.countList.completeListFailed', {}, 'Feil ved fullføring av telleliste'), type: 'error' });
    } finally {
      setCompleting(false);
    }
  };

  const completedCount = lines.filter(l => l.lineStatus >= 50).length;
  const totalCount = lines.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="animate-spin h-8 w-8 text-blue-500 mb-2" />
        <span className="text-sm text-gray-500">{t('warehouse.modules.inventory.countList.loadingLines', {}, 'Laster tellelistelinjer...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
        <p className="text-sm text-red-600 mb-3">{error}</p>
        <button
          onClick={loadLines}
          className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          <span>{t('common.buttons.tryAgain', {}, 'Prøv igjen')}</span>
        </button>
      </div>
    );
  }

  // Determine the line to display in the active box
  const activeBoxLine = activeLine || nextUncountedLine;
  const isActiveBoxCounting = activeLine !== null;

  // Step indicator labels (Order: Item → Location → Count)
  const stepLabel = confirmStep === 'item' ? t('warehouse.modules.inventory.countList.step1ConfirmItem', {}, 'Steg 1: Bekreft vare')
    : confirmStep === 'location' ? (requireItemScan ? t('warehouse.modules.inventory.countList.step2ConfirmLocation', {}, 'Steg 2: Bekreft lokasjon') : t('warehouse.modules.inventory.countList.step1ConfirmLocation', {}, 'Steg 1: Bekreft lokasjon'))
    : t('warehouse.modules.inventory.countList.registerQuantity', {}, 'Registrer antall');

  // Feil vises kraftig rødt så de fanges i øyekroken under skanning; bekreftelser forblir diskré
  const messageBanner = message && (
    <div
      role={message.type === 'error' ? 'alert' : 'status'}
      className={`flex items-center space-x-2 rounded-lg ${
        message.type === 'success'
          ? 'px-3 py-2 text-sm bg-green-50 text-green-800'
          : 'px-4 py-3 text-base font-semibold bg-red-600 text-white shadow-md'
      }`}
    >
      {message.type === 'success' ? (
        <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
      ) : (
        <AlertCircle className="h-5 w-5 flex-shrink-0" />
      )}
      <div className="min-w-0">
        <div className="break-words">{message.text}</div>
        {message.detail && (
          <div className="text-xs font-normal opacity-80 break-words">{message.detail}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Mobile scanner support — aktiv i alle steg, rutes etter tilstand */}
      <MobileBarcodeCaptureInput
        ref={mobileCaptureRef}
        enabled={barcodeScannerEnabled && !isAnyModalOpen}
        onScan={handleGlobalScan}
      />

      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t('warehouse.modules.inventory.countList.backToLists', {}, 'Tilbake til lister')}</span>
        </button>
        <div className="flex items-center gap-2">
          {/* Show active confirm settings */}
          {profileLoaded && (requireLocationScan || requireItemScan) && (
            <div className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
              <ShieldCheck className="h-3 w-3" />
              <span>
                {requireItemScan && requireLocationScan
                  ? t('warehouse.modules.inventory.countList.confirmItemAndLocationShort', {}, 'Vare+Lok')
                  : requireItemScan
                    ? t('warehouse.modules.inventory.countList.confirmItemShort', {}, 'Vare')
                    : t('warehouse.modules.inventory.countList.confirmLocationShort', {}, 'Lok')}
              </span>
            </div>
          )}
          <button
            onClick={toggleQuickMode}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium min-h-[32px] transition-colors ${
              quickMode
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Zap className="h-3.5 w-3.5" />
            <span>{t('warehouse.modules.inventory.countList.quickMode', {}, 'Hurtigtelling')}</span>
          </button>
          <button
            onClick={loadLines}
            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
            title={t('common.buttons.refresh', {}, 'Oppdater')}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Hurtigtelling: meldingen øverst, så den ikke skjules av hånden over skannefeltet */}
      {quickMode && messageBanner}

      {/* List info */}
      <div className="bg-white border rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="min-w-0">
            <h3 className="font-medium text-sm text-gray-900 truncate">
              {list.description1 || list.batchID}
            </h3>
            <p className="text-xs text-gray-500 font-mono">{list.batchID}</p>
          </div>
          {progressPercent === 100 && (
            <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0" />
          )}
        </div>
        {/* Progress */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>{completedCount} / {totalCount} linjer telt</span>
          <span>{progressPercent}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              progressPercent === 100 ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Complete button */}
        {totalCount > 0 && (
          <button
            onClick={handleCompleteList}
            disabled={completing}
            className={`mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm min-h-[44px] transition-colors ${
              completedCount === totalCount
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-orange-500 text-white hover:bg-orange-600'
            }`}
          >
            {completing ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span>
              {completedCount === totalCount
                ? t('warehouse.modules.inventory.countList.completeList', {}, 'Fullfør telleliste')
                : t('warehouse.modules.inventory.countList.completeListProgress', { '0': completedCount, '1': totalCount }, `Fullfør telleliste (${completedCount}/${totalCount} telt)`)
              }
            </span>
          </button>
        )}
      </div>

      {/* ===== ACTIVE COUNTING BOX ===== */}
      {!quickMode && activeBoxLine && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg shadow-sm p-3 sm:p-4">
          {/* MOBILE layout */}
          <div className="sm:hidden">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse flex-shrink-0" />
              <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
                {isActiveBoxCounting ? t('warehouse.modules.inventory.countList.activeCount', {}, 'Aktiv telling') : t('warehouse.modules.inventory.countList.nextLocation', {}, 'Neste lokasjon')}
              </span>
              {isActiveBoxCounting && (requireLocationScan || requireItemScan) && (
                <span className="text-xs text-orange-600 ml-auto">{stepLabel}</span>
              )}
            </div>
            <div className="flex items-center gap-2 mb-2 bg-white rounded-md p-2.5 border border-blue-200">
              <MapPin className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <span className="text-lg font-bold text-gray-900">{activeBoxLine.locationName}</span>
            </div>
            <div className="mb-2">
              <div className="font-bold text-sm text-gray-900 truncate">{activeBoxLine.itemNo}</div>
              <div className="text-xs text-gray-600 truncate">{activeBoxLine.description1}</div>
              {activeBoxLine.itemBarCode && (
                <div className="text-xs text-gray-900 font-mono font-bold truncate">
                  {t('warehouse.modules.inventory.countList.gtinLabel', {}, 'GTIN')}: {activeBoxLine.itemBarCode}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-700">
              <Package className="h-4 w-4 text-blue-600" />
              <span>{t('warehouse.modules.inventory.countList.expectedLabel', {}, 'Forventet:')} <strong>{activeBoxLine.quantityOrigin ?? 0}</strong> {activeBoxLine.itemUnitCode || 'stk'}</span>
            </div>
          </div>

          {/* DESKTOP layout - 3-column grid */}
          <div className="hidden sm:block">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" />
              <h3 className="text-base font-semibold text-blue-900">
                {isActiveBoxCounting ? t('warehouse.modules.inventory.countList.activeCount', {}, 'Aktiv telling') : t('warehouse.modules.inventory.countList.nextLocation', {}, 'Neste lokasjon')}
              </h3>
              {isActiveBoxCounting && (requireLocationScan || requireItemScan) && (
                <span className="ml-auto text-sm font-medium text-orange-600 bg-orange-50 px-2.5 py-0.5 rounded-full">
                  {stepLabel}
                </span>
              )}
            </div>
            <div className="bg-white rounded-md p-5 border border-blue-200">
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={18} className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">{t('warehouse.general.bin', {}, 'Lokasjon')}</span>
                    {isActiveBoxCounting && confirmStep === 'location' && (
                      <Scan size={14} className="text-orange-500 animate-pulse" />
                    )}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{activeBoxLine.locationName}</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Package size={18} className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">{t('warehouse.general.item', {}, 'Vare')}</span>
                    {isActiveBoxCounting && confirmStep === 'item' && (
                      <Scan size={14} className="text-orange-500 animate-pulse" />
                    )}
                  </div>
                  <div className="text-xl font-bold text-gray-900">{activeBoxLine.itemNo}</div>
                  <div className="text-sm text-gray-600 truncate">{activeBoxLine.description1}</div>
                  {activeBoxLine.itemBarCode && (
                    <div className="text-xs text-gray-900 font-mono font-bold mt-0.5 truncate">
                      {t('warehouse.modules.inventory.countList.gtinLabel', {}, 'GTIN')}: {activeBoxLine.itemBarCode}
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle size={18} className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">{t('warehouse.modules.inventory.expectedQuantity', {}, 'Forventet antall')}</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900">
                    {activeBoxLine.quantityOrigin ?? 0} <span className="text-base font-normal text-gray-500">{activeBoxLine.itemUnitCode || 'stk'}</span>
                  </div>
                  {activeBoxLine.lineStatus >= 50 && (
                    <div className="text-sm font-semibold text-green-600 mt-1">
                      {t('warehouse.modules.inventory.countList.countedLabel', { '0': activeBoxLine.quantity ?? 0 }, `Telt: ${activeBoxLine.quantity ?? 0}`)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ===== CONFIRMATION STEPS (inline in active box) ===== */}
          {isActiveBoxCounting && activeLine && activeLine.lineStatus < 50 && (
            <div className="mt-3">
              {/* Scan error */}
              {scanError && (
                <div className="flex items-center space-x-2 px-3 py-2 mb-2 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{scanError}</span>
                </div>
              )}

              {/* STEP: Location scan */}
              {confirmStep === 'location' && (
                <div className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-orange-500" />
                    <span className="text-sm font-medium text-orange-700">{t('warehouse.modules.inventory.countList.scanLocationToConfirm', {}, 'Skann lokasjon for å bekrefte')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <ScanTextInput
                      ref={locationScanRef}
                      value={locationScanInput}
                      onChange={(e) => { setLocationScanInput(e.target.value); setScanError(null); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleLocationConfirm();
                        if (e.key === 'Escape') cancelCounting();
                      }}
                      placeholder={t('warehouse.modules.inventory.countList.scanLocationPlaceholder', { '0': activeLine.locationName }, `Skann lokasjon (${activeLine.locationName})`)}
                      className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base min-h-[44px] bg-white"
                      autoFocus={!barcodeScannerEnabled}
                    />
                    <button
                      type="button"
                      onClick={() => handleLocationConfirm()}
                      disabled={!locationScanInput.trim()}
                      className={`px-4 py-2.5 rounded-lg font-medium text-sm min-h-[44px] ${
                        !locationScanInput.trim()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {t('common.buttons.confirm', {}, 'Bekreft')}
                    </button>
                    <button
                      type="button"
                      onClick={handleLocationOverride}
                      className="px-3 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium text-sm min-h-[44px]"
                    >
                      {t('warehouse.modules.inventory.countList.override', {}, 'Overstyr')}
                    </button>
                    <button
                      type="button"
                      onClick={cancelCounting}
                      className="p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP: Item scan */}
              {confirmStep === 'item' && (
                <div className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-4 w-4 text-orange-500" />
                    <span className="text-sm font-medium text-orange-700">{t('warehouse.modules.inventory.countList.scanItemToConfirm', {}, 'Skann vare for å bekrefte')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <ScanTextInput
                      ref={itemScanRef}
                      value={itemScanInput}
                      onChange={(e) => { setItemScanInput(e.target.value); setScanError(null); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleItemConfirm();
                        if (e.key === 'Escape') cancelCounting();
                      }}
                      placeholder={t('warehouse.modules.inventory.countList.scanItemPlaceholder', { '0': activeLine.itemNo }, `Skann vare (${activeLine.itemNo})`)}
                      className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base min-h-[44px] bg-white"
                      autoFocus={!barcodeScannerEnabled}
                    />
                    <button
                      type="button"
                      onClick={() => handleItemConfirm()}
                      disabled={!itemScanInput.trim()}
                      className={`px-4 py-2.5 rounded-lg font-medium text-sm min-h-[44px] ${
                        !itemScanInput.trim()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {t('common.buttons.confirm', {}, 'Bekreft')}
                    </button>
                    <button
                      type="button"
                      onClick={cancelCounting}
                      className="p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP: Count input */}
              {confirmStep === 'count' && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveCount(activeLine.lineID);
                  }}
                  className="flex items-center space-x-2"
                >
                  {/* Show confirmed badges if steps were completed */}
                  {(requireLocationScan || requireItemScan) && (
                    <div className="flex items-center gap-1 mr-1">
                      {requireLocationScan && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                          <CheckCircle2 className="h-3 w-3" />
                          {t('warehouse.modules.inventory.countList.confirmLocationShort', {}, 'Lok')}
                        </span>
                      )}
                      {requireItemScan && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                          <CheckCircle2 className="h-3 w-3" />
                          {t('warehouse.modules.inventory.countList.confirmItemShort', {}, 'Vare')}
                        </span>
                      )}
                    </div>
                  )}
                  <NumericInput
                    ref={countInputRef}
                    mode="decimal"
                    quantity
                    value={countInput}
                    onChange={(e) => setCountInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') cancelCounting();
                    }}
                    placeholder={t('warehouse.modules.inventory.countedQuantity', {}, 'Telt antall')}
                    className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base font-semibold min-h-[44px] bg-white"
                    min="0"
                    autoFocus={!barcodeScannerEnabled}
                  />
                  <button
                    type="submit"
                    disabled={saving || !countInput.trim()}
                    className={`px-5 py-2.5 rounded-lg font-medium text-sm min-h-[44px] flex items-center space-x-1 ${
                      saving || !countInput.trim()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {saving ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    <span>{t('common.buttons.save', {}, 'Lagre')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={cancelCounting}
                    className="p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Click hint when showing next uncounted (not actively counting) */}
          {!isActiveBoxCounting && nextUncountedLine && (
            <button
              onClick={() => handleStartCounting(nextUncountedLine.lineID)}
              className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg font-medium text-sm min-h-[44px] hover:bg-blue-600 transition-colors"
            >
              <Scan className="h-4 w-4" />
              <span>{t('warehouse.modules.inventory.countList.startCountingHere', {}, 'Start telling på denne lokasjonen')}</span>
            </button>
          )}
        </div>
      )}

      {/* Scanner hint */}
      {!quickMode && activeLineId === null && (
        <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <Scan className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span className="text-xs text-blue-700">{t('warehouse.modules.inventory.countList.scannerHint', {}, 'Skann en strekkode for å hoppe til riktig linje, eller velg en linje manuelt')}</span>
        </div>
      )}

      {/* Message — i hurtigtelling vises den øverst (over listekortet) */}
      {!quickMode && messageBanner}

      {/* ===== QUICK MODE PANEL (TW 36373390) ===== */}
      {quickMode && (
        <>
          {/* Skann-status + passivt inputfelt for manuell inntasting */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              {quickSaving ? (
                <Loader className="h-5 w-5 text-blue-600 animate-spin" />
              ) : (
                <Scan className="h-5 w-5 text-blue-600 animate-pulse" />
              )}
              <span className="text-sm font-semibold text-blue-900">
                {t('warehouse.modules.inventory.countList.quickModeHint', {}, 'Skann vare — hver skann teller 1')}
              </span>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const value = quickManualInput;
                setQuickManualInput('');
                handleQuickScan(value);
              }}
              className="flex items-center space-x-2"
            >
              <ScanTextInput
                value={quickManualInput}
                onChange={(e) => setQuickManualInput(e.target.value)}
                placeholder={t('warehouse.modules.inventory.countList.quickManualPlaceholder', {}, 'Skann eller skriv varenummer...')}
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base min-h-[44px] bg-white"
              />
              <button
                type="submit"
                disabled={quickSaving || !quickManualInput.trim()}
                className={`px-4 py-2.5 rounded-lg font-medium text-sm min-h-[44px] ${
                  quickSaving || !quickManualInput.trim()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {t('common.buttons.confirm', {}, 'Bekreft')}
              </button>
            </form>
          </div>

          {/* Uventet vare: hvor er varen registrert? */}
          {unknownItem && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-orange-800">
                      {t('warehouse.modules.inventory.countList.unknownItemTitle', {}, 'Varen står ikke på tellelisten')}
                    </div>
                    <div className="text-sm font-mono font-bold text-gray-900">{unknownItem.itemNo}</div>
                  </div>
                </div>
                <button
                  onClick={() => setUnknownItem(null)}
                  className="p-2 rounded-lg hover:bg-orange-100 min-h-[36px] min-w-[36px] flex items-center justify-center"
                >
                  <X className="h-4 w-4 text-orange-700" />
                </button>
              </div>
              <div className="mt-2 pl-7">
                {unknownItem.loading ? (
                  <div className="flex items-center gap-2 text-xs text-orange-700">
                    <Loader className="h-3.5 w-3.5 animate-spin" />
                    <span>{t('warehouse.modules.inventory.countList.unknownItemSearching', {}, 'Slår opp lokasjoner...')}</span>
                  </div>
                ) : unknownItem.locations.length > 0 ? (
                  <>
                    <div className="text-xs font-medium text-orange-800 mb-1">
                      {t('warehouse.modules.inventory.countList.unknownItemLocations', {}, 'Compass har varen registrert på:')}
                    </div>
                    <div className="space-y-1">
                      {unknownItem.locations.slice(0, 5).map((loc, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm bg-white rounded px-2 py-1.5 border border-orange-100">
                          <MapPin className="h-4 w-4 text-orange-600 flex-shrink-0" />
                          <span className="font-bold text-gray-900">{loc.locationName}</span>
                          <span className="text-gray-600 ml-auto">{loc.totalQuantity} {t('warehouse.modules.inventory.countList.pcs', {}, 'stk')}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-orange-700">
                    {t('warehouse.modules.inventory.countList.unknownItemNoLocations', {}, 'Ingen tilgjengelig saldo registrert for denne varen')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Siste talte (maks 10) */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {t('warehouse.modules.inventory.countList.lastCounted', {}, 'Siste talte')}
              </span>
              <button
                onClick={() => { setCountedFilter(''); setShowCountedModal(true); }}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium min-h-[32px] px-2"
              >
                <List className="h-3.5 w-3.5" />
                <span>{t('warehouse.modules.inventory.countList.showCountedLines', { '0': completedCount }, `Vis talte linjer (${completedCount})`)}</span>
              </button>
            </div>
            {recentCounts.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {t('warehouse.modules.inventory.countList.noCountedYet', {}, 'Ingen varer talt ennå i denne økten')}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentCounts.map((rc) => (
                  <div key={rc.lineID} className="flex items-center px-3 py-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mr-2" />
                    <div className="flex-1 min-w-0">
                      {/* Varenr og beskrivelse på hver sin linje — smal skanner kuttet ellers varenummeret */}
                      <div className="font-mono text-sm font-medium break-all">{rc.itemNo}</div>
                      {rc.description1 && (
                        <div className="text-xs text-gray-600 break-words">{rc.description1}</div>
                      )}
                      {rc.locationName && (
                        <div className="text-xs text-gray-500">{rc.locationName}</div>
                      )}
                    </div>
                    <span className="text-sm font-bold text-green-700 flex-shrink-0 ml-2">
                      {rc.quantity} {rc.itemUnitCode || 'stk'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Lines list */}
      {!quickMode && (
      <div className="bg-white border rounded-lg divide-y divide-gray-100 overflow-hidden">
        {lines.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">{t('warehouse.modules.inventory.countList.noLinesInList', {}, 'Ingen linjer i denne tellelisten')}</p>
          </div>
        ) : (
          lines.map((line) => {
            const isCounted = line.lineStatus >= 50;
            const isActive = activeLineId === line.lineID;

            return (
              <div
                key={line.lineID}
                className={`${isActive ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : isCounted ? 'bg-green-50/30' : 'hover:bg-gray-50'}`}
              >
                <button
                  onClick={() => {
                    if (isCounted && !isActive) {
                      // Allow reset only if list is not completed
                      if (list.orderStatus < 50) {
                        handleResetLine(line.lineID);
                      }
                    } else if (!isCounted) {
                      handleStartCounting(line.lineID);
                    }
                  }}
                  className="w-full text-left p-3 flex items-center"
                  disabled={isCounted && list.orderStatus >= 50}
                >
                  <div className="flex-shrink-0 mr-3">
                    {isCounted ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : isActive ? (
                      <ChevronDown className="h-5 w-5 text-blue-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <span className="font-bold text-base text-gray-900">{line.locationName}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-sm font-medium truncate">{line.itemNo}</span>
                      <span className="text-xs text-gray-500 truncate">{line.description1}</span>
                    </div>
                    {line.itemBarCode && (
                      <div className="font-mono text-xs text-gray-900 font-bold truncate">
                        {t('warehouse.modules.inventory.countList.gtinLabel', {}, 'GTIN')}: {line.itemBarCode}
                      </div>
                    )}
                    <div className="flex items-center space-x-3 mt-0.5 text-xs text-gray-500">
                      <span>{t('warehouse.modules.inventory.countList.expectedLabel', {}, 'Forventet:')} {line.quantityOrigin ?? 0} {line.itemUnitCode || 'stk'}</span>
                      {isCounted && (
                        <span className="text-green-700 font-medium">
                          {t('warehouse.modules.inventory.countList.countedLabel', { '0': line.quantity ?? 0 }, `Telt: ${line.quantity ?? 0}`)}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            );
          })
        )}
      </div>
      )}

      {/* ===== COUNTED LINES MODAL (hurtigmodus — aldri alle linjer i selve bildet) ===== */}
      {showCountedModal && (() => {
        const countedLines = lines.filter(l => l.lineStatus >= 50);
        const filter = countedFilter.trim().toLowerCase();
        const filtered = filter
          ? countedLines.filter(l =>
              l.itemNo?.toLowerCase().includes(filter) ||
              l.description1?.toLowerCase().includes(filter) ||
              l.locationName?.toLowerCase().includes(filter))
          : countedLines;
        const visible = filtered.slice(0, COUNTED_MODAL_RENDER_MAX);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 border-b flex items-center justify-between flex-shrink-0">
                <h3 className="font-semibold text-gray-900 text-base">
                  {t('warehouse.modules.inventory.countList.countedLinesTitle', {}, 'Talte linjer')} ({countedLines.length})
                </h3>
                <button
                  onClick={() => setShowCountedModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 min-h-[36px] min-w-[36px] flex items-center justify-center"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {/* Search */}
              <div className="px-4 py-2 border-b flex-shrink-0">
                <div className="relative">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <ScanTextInput
                    value={countedFilter}
                    onChange={(e) => setCountedFilter(e.target.value)}
                    placeholder={t('warehouse.modules.inventory.countList.searchCounted', {}, 'Søk i talte linjer...')}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-h-[40px]"
                  />
                </div>
              </div>
              {/* Body */}
              <div className="overflow-y-auto flex-1">
                {visible.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-500">
                    {t('warehouse.modules.inventory.countList.noCountedLines', {}, 'Ingen talte linjer')}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {visible.map((line) => (
                      <div key={line.lineID} className="flex items-center px-4 py-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mr-2" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium truncate">{line.itemNo}</span>
                            <span className="text-xs text-gray-500 truncate">{line.description1}</span>
                          </div>
                          <div className="text-xs text-gray-500">{line.locationName}</div>
                        </div>
                        <span className="text-sm font-bold text-green-700 flex-shrink-0 ml-2">
                          {line.quantity ?? 0} {line.itemUnitCode || 'stk'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Footer */}
              <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between flex-shrink-0">
                <span className="text-xs text-gray-500">
                  {filtered.length > visible.length
                    ? t('warehouse.modules.inventory.countList.showingFirstN', { '0': visible.length, '1': filtered.length }, `Viser ${visible.length} av ${filtered.length} — søk for å avgrense`)
                    : `${filtered.length} / ${countedLines.length}`}
                </span>
                <button
                  onClick={() => setShowCountedModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-300 min-h-[40px]"
                >
                  {t('common.buttons.close', {}, 'Lukk')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
